import {
	ChangeDetectionStrategy,
	Component,
	ElementRef,
	OnInit,
	effect,
	input,
	output,
	signal,
	viewChild
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { cloneHelper } from 'lib/helpers/clone-helper.class';
import { observableHelper } from 'lib/helpers/observable-helper.class.js';
import { CoreComponent } from 'lib/ui/core/core.component.js';
import { Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

export interface ISearchInputResult {
	readonly search: string;
	readonly searchWords: readonly string[];
}

export type SearchInputDesign = 'default' | 'alt';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: 'lib-search-input',
	styleUrls: ['./search-input.component.scss'],
	templateUrl: 'search-input.component.html',
	standalone: false
})
export class SearchInputComponent extends CoreComponent implements OnInit {
	public readonly searchInputElem = viewChild<ElementRef>('searchInput');

	public readonly minSearchChars = input<number>();
	public readonly debounceTime = input<number>(200);
	public readonly placeholder = input<string>();
	public readonly defaultValue = input<string>();
	public readonly design = input<SearchInputDesign>('default');
	public readonly focusOnInit = input<boolean>(false);

	public readonly search = output<ISearchInputResult>();

	private currentSubscription?: Subscription;
	private readonly isFirstInit = signal(true);

	public readonly searchControl = new UntypedFormControl();

	constructor() {
		super();

		effect(() => {
			// resubscribe when debounce time changes
			const debounceTime = this.debounceTime();

			if (this.currentSubscription) {
				this.currentSubscription.unsubscribe();
			}

			this.subscribeToSearch(debounceTime);
		});

		effect(() => {
			const searchElem = this.searchInputElem();

			if (this.focusOnInit()) {
				// do not autofocus on mobiles
				if (searchElem && this.isFirstInit() && !this.isMobileView()) {
					searchElem.nativeElement.focus();
					this.isFirstInit.set(false);
				}
			}
		});
	}

	ngOnInit(): void {
		this.subscribeToSearch(this.debounceTime());
		const defaultValue = this.defaultValue();

		if (defaultValue) {
			this.searchControl.setValue(defaultValue, {
				emitEvent: false
			});
		}
	}

	clearSearch(): void {
		this.searchControl.setValue('');
	}

	private subscribeToSearch(debounceTimeMs: number): void {
		this.currentSubscription = super.subscribeToObservable(
			this.searchControl.valueChanges.pipe(
				observableHelper.customDebounceTime(debounceTimeMs),
				map((searchRaw: string | undefined) => {
					const search = cloneHelper.clone(searchRaw?.trim());

					if (!search) {
						this.search.emit({
							search: '',
							searchWords: []
						});
						return;
					}

					if (search.length >= (this.minSearchChars() ?? 0)) {
						const searchWords = search.split(' ');

						if (Array.isArray(searchWords) && searchWords?.length) {
							this.search.emit({
								search: search,
								searchWords: searchWords
							});
						} else {
							this.search.emit({
								search: search,
								searchWords: []
							});
						}
					}
				})
			)
		);
	}
}
